import { useEffect, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Plus, Tags } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import {
  attendeeFieldsSetupQueryOptions,
  fieldTypeLabel,
  FIELD_TYPES,
  slugifyKey,
  upsertAttendeeField,
  type AttendeeField,
  type FieldOption,
  type FieldType,
  type UpsertFieldInput,
} from "@/core/attendee-fields"
import { ApiError } from "@/core/api"
import { ResponsiveSheet } from "@/components/ui/ResponsiveSheet"
import {
  Card,
  Field,
  GhostButton,
  PrimaryButton,
  Select,
  SettingsPage,
  Textarea,
  TextInput,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/attendee-fields")({
  component: AttendeeFieldsView,
})

function AttendeeFieldsView() {
  const { eventId } = useActiveEvent()
  const q = useQuery({
    ...attendeeFieldsSetupQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const fields = q.data?.fields ?? []

  const [editing, setEditing] = useState<AttendeeField | "new" | null>(null)

  return (
    <SettingsPage
      title="Custom Attendee Fields"
      description="Custom data you collect on each attendee — used for groups, directories and exports."
      actions={
        <PrimaryButton onClick={() => setEditing("new")} disabled={!eventId}>
          <Plus className="h-4 w-4" />
          New field
        </PrimaryButton>
      }
    >
      {q.isError && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {(q.error as Error)?.message ?? "Failed to load fields"}
          </p>
        </Card>
      )}

      {fields.length === 0 && !q.isLoading ? (
        <Card>
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-plum-100 text-plum-700/60 dark:bg-white/10 dark:text-plum-200/50">
              <Tags className="h-5 w-5" />
            </span>
            <p className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">
              No custom fields yet
            </p>
            <p className="max-w-xs text-[12.5px] text-plum-700/55 dark:text-plum-200/40">
              Add fields like dietary needs, t-shirt size or VIP tier — then build automatic
              groups from them.
            </p>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="divide-y divide-plum-200/50 dark:divide-white/5">
            {fields.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setEditing(f)}
                className="flex w-full items-center gap-3 py-2.5 text-left transition hover:bg-plum-50/60 dark:hover:bg-white/[0.03]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                      {f.label}
                    </span>
                    <code className="rounded bg-plum-100 px-1.5 py-0.5 text-[11px] text-plum-700/70 dark:bg-white/10 dark:text-plum-200/55">
                      {f.key}
                    </code>
                  </div>
                  <p className="mt-0.5 text-[11.5px] text-plum-700/50 dark:text-plum-200/35">
                    {fieldTypeLabel(f.type)}
                    {f.options?.length ? ` · ${f.options.length} options` : ""}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {editing && eventId && (
        <FieldSheet
          eventId={eventId}
          field={editing === "new" ? null : editing}
          existingKeys={fields.map((f) => f.key)}
          onClose={() => setEditing(null)}
        />
      )}
    </SettingsPage>
  )
}

const SELECT_TYPES: FieldType[] = ["single_select", "multi_select"]

function FieldSheet({
  eventId,
  field,
  existingKeys,
  onClose,
}: {
  eventId: string
  field: AttendeeField | null
  existingKeys: string[]
  onClose: () => void
}) {
  const qc = useQueryClient()
  const isEdit = !!field

  const [label, setLabel] = useState(field?.label ?? "")
  const [key, setKey] = useState(field?.key ?? "")
  const [keyTouched, setKeyTouched] = useState(isEdit)
  const [type, setType] = useState<FieldType>(field?.type ?? "short_text")
  // Visibility picker is hidden (staged feature) — preserve any existing value,
  // default new fields to organizer-only.
  const visibility = field?.visibility ?? "admin_only"
  const [optionsText, setOptionsText] = useState(
    (field?.options ?? []).map((o) => (o.value === o.label ? o.value : `${o.value} = ${o.label}`)).join("\n"),
  )

  // Auto-derive key from label until the user edits it directly (create only).
  useEffect(() => {
    if (!keyTouched) setKey(slugifyKey(label))
  }, [label, keyTouched])

  const isSelect = SELECT_TYPES.includes(type)

  const save = useMutation({
    mutationFn: () => {
      const trimmedKey = slugifyKey(key)
      if (!label.trim()) throw new ApiError("Label is required", 400)
      if (!trimmedKey) throw new ApiError("Key is required", 400)
      let options: FieldOption[] | undefined
      if (isSelect) {
        options = optionsText
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean)
          .map((l) => {
            const eq = l.indexOf("=")
            if (eq === -1) return { value: l, label: l }
            return { value: l.slice(0, eq).trim(), label: l.slice(eq + 1).trim() }
          })
          .filter((o) => o.value && o.label)
        if (options.length === 0) throw new ApiError("Select fields need at least one option", 400)
      }
      const input: UpsertFieldInput = {
        ...(field ? { id: field.id } : {}),
        key: trimmedKey,
        label: label.trim(),
        type,
        visibility,
        options,
      }
      return upsertAttendeeField(eventId, input)
    },
    onSuccess: () => {
      toast.success(isEdit ? "Field updated" : "Field created")
      void qc.invalidateQueries({
        queryKey: ["resource", "attendee_field_definitions", { eventId, mode: "setup" }],
      })
      onClose()
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Failed to save field"),
  })

  const duplicateKey =
    !isEdit && existingKeys.includes(slugifyKey(key)) && slugifyKey(key) !== ""

  return (
    <ResponsiveSheet
      open
      onClose={onClose}
      title={isEdit ? "Edit field" : "New attendee field"}
      subtitle={isEdit ? field?.key : "Define a custom attendee data point"}
      icon={<Tags className="h-4 w-4" />}
      footer={
        <div className="flex justify-end gap-2">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton
            onClick={() => save.mutate()}
            disabled={save.isPending || duplicateKey}
          >
            {save.isPending ? "Saving…" : isEdit ? "Save field" : "Create field"}
          </PrimaryButton>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Label">
          <TextInput
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Dietary needs"
            autoFocus
          />
        </Field>
        <Field
          label="Key"
          hint={
            isEdit
              ? "The key is fixed after creation."
              : duplicateKey
                ? "A field with this key already exists."
                : "Stable identifier used in rules and exports."
          }
        >
          <TextInput
            value={key}
            disabled={isEdit}
            onChange={(e) => {
              setKeyTouched(true)
              setKey(e.target.value)
            }}
            onBlur={() => setKey(slugifyKey(key))}
            placeholder="dietary_needs"
            className={duplicateKey ? "border-rose-400 focus:border-rose-400 focus:ring-rose-400/20" : ""}
          />
        </Field>
        <Field label="Type">
          <Select value={type} onChange={(e) => setType(e.target.value as FieldType)}>
            {FIELD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>
        {isSelect && (
          <Field label="Options" hint="One per line. Use `value = Label` to set a separate label.">
            <Textarea
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              placeholder={"vegan = Vegan\nhalal = Halal\nnone = No restrictions"}
              rows={5}
            />
          </Field>
        )}
        {/* Visibility is staged: the members_only / directory levels aren't yet
            surfaced to attendees, so we keep every field organizer-only (the
            admin_only default) and hide the picker until the guest-facing read
            layer lands. Backend enum/column stay intact. */}
      </div>
    </ResponsiveSheet>
  )
}
